import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { Button } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { executeLaravelFrontAPI } from "../../Utils";

/**
 * LocalityConfigurationTable
 *
 * Fetches projectcountreportbyconfiguration (per_city) using the selected city
 * read from cookie "selectedCityCookie" and displays counts grouped into 1-5 BHK columns.
 */

const LocalityConfigurationTable = () => {
  const bhkTypes = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK"];

  const [selectedCity] = useState(() => Cookies.get("selectedCityCookie") || "pune");

  const [countsMap, setCountsMap] = useState({}); // { locality: { "1 BHK": n, ... } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toTitleCase = (s) => {
    if (!s && s !== "") return "";
    const str = String(s).trim();
    if (!str) return str;
    return str
      .split(/\s+/)
      .map((w) => {
        if (w === w.toUpperCase()) return w;
        return w[0]?.toUpperCase() + w.slice(1).toLowerCase();
      })
      .join(" ");
  };

  const parseApiData = (apiData) => {
    const canonicalByLower = {};
    const result = {};

    Object.entries(apiData || {}).forEach(([rawLocality, bhkObj]) => {
      if (rawLocality == null || rawLocality === "") return;

      const canonical = toTitleCase(rawLocality);
      const lower = canonical.toLowerCase();

      if (!canonicalByLower[lower]) canonicalByLower[lower] = canonical;
      const finalLocality = canonicalByLower[lower];

      if (!result[finalLocality]) result[finalLocality] = {};

      if (bhkObj && typeof bhkObj === "object") {
        Object.entries(bhkObj).forEach(([cfgKey, cfgVal]) => {
          const parsed = parseFloat(cfgKey);
          if (Number.isNaN(parsed)) return;

          const floored = Math.floor(parsed);
          if (floored < 1 || floored > 5) return;

          const bucket = `${floored} BHK`;
          const value = Number(cfgVal) || 0;

          result[finalLocality][bucket] = (result[finalLocality][bucket] || 0) + value;
        });
      }
    });

    return result;
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchCounts = async () => {
      setLoading(true);
      setError(null);

      try {
        // NOTE: executeLaravelFrontAPI signature: (apiEndPoint, data = '', apiMethod = 'POST')
        // We call with endpoint name (no base URL) and use GET so util builds query string.
        const apiEndpoint = "projectcountreportbyconfiguration"; // change if your base URL doesn't already include v1
        const params = {
          per_page: 5000,
          city: selectedCity || "pune",
          // bwusercheck will be auto-added by util if running localhost; adding explicitly is safe:
          bwusercheck: 1,
        };

        // helper returns parsed JSON (or '' on non-200)
        const json = await executeLaravelFrontAPI(apiEndpoint, params, "GET");

        // Handle different response structures:
        // 1. API returns array: [{ locality1: {...}, locality2: {...} }]
        // 2. API returns object with data: { status: 200, success: true, data: {...} }
        let apiData = null;
        
        if (Array.isArray(json) && json.length > 0) {
          // Response is an array, use first element
          apiData = json[0];
        } else if (json && typeof json === "object" && json.data) {
          // Response has data property
          apiData = json.data;
        } else if (json && typeof json === "object" && !Array.isArray(json)) {
          // Response is directly the data object
          apiData = json;
        }

        if (!apiData || typeof apiData !== "object") {
          console.error("Unexpected API response:", json);
          throw new Error("Unexpected response shape from API");
        }

        if (cancelled) return;

        const parsed = parseApiData(apiData);
        setCountsMap(parsed);
      } catch (err) {
        if (err && err.name === "AbortError") {
          return;
        }
        console.error("Failed fetching locality counts:", err);
        setError(err?.message || String(err));
        setCountsMap({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCounts();

    return () => {
      cancelled = true;
      controller.abort(); // not used by util but safe to keep
    };
  }, [selectedCity]);

  const allLocalities = useMemo(() => Object.keys(countsMap).sort((a, b) => a.localeCompare(b)), [countsMap]);

  // Export data to CSV
  const exportToCSV = () => {
    if (allLocalities.length === 0) {
      alert("No data to export");
      return;
    }

    // Create CSV header
    const headers = ["Locality", ...bhkTypes];
    
    // Create CSV rows
    const rows = allLocalities.map((loc) => {
      const row = [loc];
      bhkTypes.forEach((bhk) => {
        const count = countsMap?.[loc]?.[bhk] ?? 0;
        row.push(count);
      });
      return row;
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `locality_configurations_${selectedCity}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          Locality Configurations <small className="text-muted">City: {selectedCity}</small>
        </h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={exportToCSV}
          disabled={loading || allLocalities.length === 0}
        >
          <Icon icon="mdi:file-export-outline" className="me-1" />
          Export CSV
        </Button>
      </div>

      {loading && <div className="text-muted mb-2">Loading counts...</div>}
      {error && <div className="alert alert-danger">Error loading data: {error}</div>}

      <div style={{ overflowX: "auto" }}>
        <table
          className="table table-bordered table-hover text-center align-middle"
          style={{ borderCollapse: "collapse", minWidth: "720px" }}
        >
          <thead className="table-secondary">
            <tr>
              <th style={{ width: "220px", textAlign: "left", paddingLeft: 12 }}>Locality</th>
              {bhkTypes.map((bhk) => (
                <th key={bhk}>{bhk}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {allLocalities.length === 0 && !loading ? (
              <tr>
                <td colSpan={1 + bhkTypes.length} className="text-center text-muted py-4">
                  No localities found for city "{selectedCity}".
                </td>
              </tr>
            ) : (
              allLocalities.map((loc) => (
                <tr key={loc}>
                  <td className="text-start fw-semibold">{loc}</td>
                  {bhkTypes.map((bhk) => {
                    const count = countsMap?.[loc]?.[bhk] ?? 0;
                    return (
                      <td
                        key={`${loc}-${bhk}`}
                        style={{
                          cursor: "default",
                          height: "48px",
                          userSelect: "none",
                          transition: "background 0.15s ease",
                        }}
                      >
                        {loading ? <span className="text-muted">⏳</span> : count > 0 ? <span className="fw-bold text-primary">{count}</span> : <span className="text-muted">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LocalityConfigurationTable;
